// Infographic tab — single-page, print-ready executive summary.
// Enterprise report layout: branded header band, KPI strip, trend charts
// (mentions area + sentiment lines), source-mix donut, and ranked breakdowns.
// Print button uses native window.print() with a print CSS block that hides
// the rest of the dashboard chrome (see brand-mentions.scss @media print).
import React, { useEffect, useState } from 'react';
import { bp_sentiment, bp_sources, bp_influencers, bp_chart } from '../../../services';
import { formatNumber, unwrap } from './_shared';
import { Icon } from './_icons';
import './brand-mentions.scss';

const DONUT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
                      '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#f97316',
                      '#14b8a6', '#a855f7'];

function Kpi({ tone, icon, color, label, value, sub }) {
    return (
        <div className={`bm_kpi ${tone}`}>
            <div className="bm_kpi_icon"><Icon name={icon} size={16} color={color} /></div>
            <div className="bm_kpi_label">{label}</div>
            <div className="bm_kpi_value">{value}</div>
            {sub && <div className="bm_kpi_sub">{sub}</div>}
        </div>
    );
}

function RankRow({ no, label, fillPct, val, score, color }) {
    return (
        <div className="bm_rank_row">
            <span className="bm_rank_no">{no}</span>
            <div className="bm_rank_main">
                <div className="bm_rank_label">{label}</div>
                <div className="bm_rank_track">
                    <div className="bm_rank_fill"
                         style={{ width: `${Math.max(3, Math.min(100, fillPct))}%`,
                                  background: color || undefined }} />
                </div>
            </div>
            {score != null
                ? <span className="bm_rank_score">{score}</span>
                : <span className="bm_rank_val">{val}</span>}
        </div>
    );
}

export default function InfographicTab() {
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState({ total: 0, by_sentiment: {}, by_narrative: {} });
    const [sources, setSources] = useState([]);
    const [influencers, setInfluencers] = useState([]);
    const [chart, setChart] = useState({ mentions_reach_series: [] });

    useEffect(() => {
        Promise.allSettled([
            bp_sentiment({ days }),
            bp_sources({ days }),
            bp_influencers({ days, limit: 10 }),
            bp_chart({ days, bucket: 'days' }),
        ]).then(([s, src, inf, c]) => {
            const ok = r => r?.status === 'fulfilled' ? r.value : null;
            setSummary(unwrap(ok(s)) || {});
            setSources((unwrap(ok(src)) || {}).sources || []);
            setInfluencers((unwrap(ok(inf)) || {}).influencers || []);
            setChart(unwrap(ok(c)) || {});
        });
    }, [days]);

    const total = summary.total || 0;
    const pos = summary.by_sentiment?.positive || 0;
    const neg = summary.by_sentiment?.negative || 0;
    const totalSourceMentions = sources.reduce((s, x) => s + (x.mentions || 0), 0) || 1;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

    const series = chart.mentions_reach_series || [];
    const sentimentSeries = chart.sentiment_series || [];
    const totalReach = series.reduce((a, d) => a + (d.reach || 0), 0);
    const today = new Date().toLocaleDateString(undefined,
        { year: 'numeric', month: 'short', day: 'numeric' });

    // ---- mentions area chart ----
    const w = 360, h = 110, pad = 8;
    const yMax = Math.max(1, ...series.map(d => d.mentions || 0));
    const x = i => pad + (i * (w - 2 * pad)) / Math.max(1, series.length - 1);
    const y = v => h - pad - ((v / yMax) * (h - 2 * pad));
    const linePath = series.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(d.mentions || 0)}`).join(' ');
    const areaPath = series.length
        ? `${linePath} L ${x(series.length - 1)} ${h - pad} L ${x(0)} ${h - pad} Z`
        : '';

    // ---- sentiment dual-line ----
    const sentMax = Math.max(1, ...sentimentSeries.flatMap(d => [d.positive || 0, d.negative || 0, d.neutral || 0]));
    const sx = i => pad + (i * (w - 2 * pad)) / Math.max(1, sentimentSeries.length - 1);
    const sy = v => h - pad - ((v / sentMax) * (h - 2 * pad));
    const posPath = sentimentSeries.map((d, i) => `${i === 0 ? 'M' : 'L'} ${sx(i)} ${sy(d.positive || 0)}`).join(' ');
    const negPath = sentimentSeries.map((d, i) => `${i === 0 ? 'M' : 'L'} ${sx(i)} ${sy(d.negative || 0)}`).join(' ');

    // ---- source mix donut ----
    const pieSlices = (sources || []).slice(0, 12).map((s, i) => ({
        label: s.platform || 'other',
        value: s.mentions || 0,
        pct: ((s.mentions || 0) / totalSourceMentions) * 100,
        color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));
    const pieSize = 150, pcx = pieSize / 2, pcy = pieSize / 2, pieR = 58, pieStroke = 24;
    let pieAcc = 0;

    const narratives = Object.entries(summary.by_narrative || {})
        .sort((a, b) => b[1] - a[1]).slice(0, 6);
    const narrMax = Math.max(1, ...narratives.map(([, v]) => v));
    const topSources = sources.slice(0, 6);
    const topInf = influencers.slice(0, 6);

    return (
        <div className="bm_info">
            {/* ---- report header band ---- */}
            <div className="bm_info_header">
                <div className="bm_info_brand">
                    <div className="bm_info_mark" aria-hidden="true">
                        <svg width="24" height="24" viewBox="0 0 32 32">
                            <circle cx="16" cy="16" r="12" fill="none" stroke="#7c3aed" strokeWidth="2" opacity="0.55" />
                            <circle cx="16" cy="16" r="6" fill="none" stroke="#7c3aed" strokeWidth="2" />
                            <circle cx="16" cy="16" r="2" fill="#7c3aed" />
                        </svg>
                    </div>
                    <div className="bm_info_titles">
                        <h3>Brand Protection — Executive Summary</h3>
                        <div className="bm_info_sub">
                            Last {days} days · Generated {today} · NOVA AI
                        </div>
                    </div>
                </div>
                <div className="bm_info_controls">
                    <select value={days} onChange={e => setDays(Number(e.target.value))}>
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                    <button className="bm_info_print" onClick={() => window.print()}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        Print / Save as PDF
                    </button>
                </div>
            </div>

            <div className="bm_info_body">
                {/* ---- KPI strip ---- */}
                <div className="bm_info_kpis">
                    <Kpi tone="accent" icon="bar-chart" color="#3b82f6"
                         label="Total mentions" value={formatNumber(total)}
                         sub={`across ${sources.length} platform${sources.length === 1 ? '' : 's'}`} />
                    <Kpi tone="pos" icon="trending-up" color="#16a34a"
                         label="Positive" value={formatNumber(pos)} sub={`${pct(pos)}% of total`} />
                    <Kpi tone="neg" icon="alert" color="#dc2626"
                         label="Negative" value={formatNumber(neg)} sub={`${pct(neg)}% of total`} />
                    <Kpi tone="violet" icon="broadcast" color="#7c3aed"
                         label="Estimated reach" value={formatNumber(Math.round(totalReach))}
                         sub="cumulative across mentions" />
                </div>

                {/* ---- trends ---- */}
                <div className="bm_info_section_title">Trends</div>
                <div className="bm_info_charts">
                    <div className="bm_info_card">
                        <div className="bm_info_section_title">Mentions over time</div>
                        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%' }}>
                            <defs>
                                <linearGradient id="bm_area" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad}
                                  stroke="#e2e8f0" strokeWidth="1" />
                            {series.length > 0 && <path d={areaPath} fill="url(#bm_area)" />}
                            {series.length > 0 && <path d={linePath} stroke="#3b82f6" strokeWidth="2" fill="none" />}
                            {series.length === 0 && (
                                <text x={w / 2} y={h / 2} textAnchor="middle" fontSize="11" fill="#94a3b8">no data</text>
                            )}
                        </svg>
                    </div>
                    <div className="bm_info_card">
                        <div className="bm_info_section_title">Sentiment over time</div>
                        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%' }}>
                            <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad}
                                  stroke="#e2e8f0" strokeWidth="1" />
                            {sentimentSeries.length > 0 && (
                                <>
                                    <path d={posPath} stroke="#16a34a" strokeWidth="2" fill="none" />
                                    <path d={negPath} stroke="#dc2626" strokeWidth="2" fill="none" />
                                </>
                            )}
                            {sentimentSeries.length === 0 && (
                                <text x={w / 2} y={h / 2} textAnchor="middle" fontSize="11" fill="#94a3b8">no data</text>
                            )}
                        </svg>
                        <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11, color: '#475569' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 9, height: 9, borderRadius: 2, background: '#16a34a' }} /> Positive
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 9, height: 9, borderRadius: 2, background: '#dc2626' }} /> Negative
                            </span>
                        </div>
                    </div>
                </div>

                {/* ---- breakdown row 1: source mix + active sources ---- */}
                <div className="bm_info_section_title">Breakdown</div>
                <div className="bm_info_breakdown">
                    <div className="bm_info_card">
                        <div className="bm_info_section_title">Source mix</div>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                            <svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`} style={{ flexShrink: 0 }}>
                                {pieSlices.map((s, i) => {
                                    const frac = s.value / totalSourceMentions;
                                    const a0 = pieAcc * 2 * Math.PI - Math.PI / 2;
                                    const a1 = (pieAcc + frac) * 2 * Math.PI - Math.PI / 2;
                                    pieAcc += frac;
                                    const x0 = pcx + pieR * Math.cos(a0), y0 = pcy + pieR * Math.sin(a0);
                                    const x1 = pcx + pieR * Math.cos(a1), y1 = pcy + pieR * Math.sin(a1);
                                    const large = frac > 0.5 ? 1 : 0;
                                    return (
                                        <path key={i}
                                              d={`M ${x0} ${y0} A ${pieR} ${pieR} 0 ${large} 1 ${x1} ${y1}`}
                                              stroke={s.color} strokeWidth={pieStroke} fill="none" strokeLinecap="butt">
                                            <title>{s.label} · {s.value} ({s.pct.toFixed(1)}%)</title>
                                        </path>
                                    );
                                })}
                                <text x={pcx} y={pcy - 4} textAnchor="middle" fontSize="20" fontWeight="700" fill="#0f172a">
                                    {formatNumber(total)}
                                </text>
                                <text x={pcx} y={pcy + 12} textAnchor="middle" fontSize="9" fill="#94a3b8"
                                      style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    mentions
                                </text>
                            </svg>
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {pieSlices.slice(0, 6).map((s, i) => (
                                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center',
                                                           gap: 7, fontSize: 12, color: '#475569' }}>
                                        <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                                        <span style={{ textTransform: 'capitalize', flex: 1, overflow: 'hidden',
                                                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                                        <strong style={{ color: '#0f172a' }}>{s.pct.toFixed(0)}%</strong>
                                    </span>
                                ))}
                                {pieSlices.length === 0 && <span className="bm_info_empty">No source data</span>}
                            </div>
                        </div>
                    </div>

                    <div className="bm_info_card">
                        <div className="bm_info_section_title">Most active sources</div>
                        {topSources.map((s, i) => (
                            <RankRow key={s.platform} no={i + 1} label={s.platform}
                                     fillPct={(s.mentions / totalSourceMentions) * 100}
                                     val={`${s.mentions} · ${((s.mentions / totalSourceMentions) * 100).toFixed(0)}%`} />
                        ))}
                        {topSources.length === 0 && <div className="bm_info_empty">No source data</div>}
                    </div>
                </div>

                {/* ---- breakdown row 2: narratives + influencers ---- */}
                <div className="bm_info_breakdown">
                    <div className="bm_info_card">
                        <div className="bm_info_section_title">Top narratives</div>
                        {narratives.map(([k, v], i) => (
                            <RankRow key={k} no={i + 1} label={k.replace(/_/g, ' ')}
                                     fillPct={(v / narrMax) * 100} val={String(v)} />
                        ))}
                        {narratives.length === 0 && <div className="bm_info_empty">No narrative data</div>}
                    </div>

                    <div className="bm_info_card">
                        <div className="bm_info_section_title">Most influential</div>
                        {topInf.map((inf, i) => (
                            <div key={inf.handle + inf.platform} className="bm_rank_row">
                                <span className="bm_rank_no">{i + 1}</span>
                                <div className="bm_rank_main">
                                    <div className="bm_rank_label">{inf.handle}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2,
                                                  textTransform: 'capitalize' }}>{inf.platform}</div>
                                </div>
                                <span className="bm_rank_score">{inf.avg_influence_score}/10</span>
                            </div>
                        ))}
                        {topInf.length === 0 && <div className="bm_info_empty">No influencer data</div>}
                    </div>
                </div>
            </div>

            <div className="bm_info_footer">
                <span>NOVA AI · Brand Protection — Confidential</span>
                <span>Generated {today}</span>
            </div>
        </div>
    );
}
